import React, { useMemo } from "react";
import { buildSingleGoogleMapsURL } from "../utils/mapUtils";
import {
  capitalizeCompanyName,
  getDataSourceBadge,
  formatMiles,
} from "../utils/formatting.js";

const getTruckTypeLabel = (code) => {
  const truckTypes = {
    V: "Van",
    SV: "Box Truck",
    VINT: "Van (Interstate)",
    SD: "Step Deck",
    MX: "Mixed",
    HS: "Hot Shot",
    AC: "Auto Carrier",
    LB: "Lowboy",
    "F+T": "Flatbed w/ Tarp",
    F: "Flatbed",
    FINT: "Flatbed (Interstate)",
    DD: "Double Drop",
    "V+V": "Van + Van",
    "V+A": "Van + Auto",
    CRG: "Cargo Van",
    HB: "Hopper Bottom",
    LA: "Landoll",
    PO: "Power Only",
    R: "Reefer",
    RINT: "Reefer (Interstate)",
    RGN: "Removable Gooseneck",
    CV: "Conestoga Van",
    DT: "Dump Truck",
    TNK: "Tanker",
    "F+S": "Flatbed w/ Straps",
    PNEU: "Pneumatic",
    CONT: "Container",
    OTHER: "Other",
  };
  return truckTypes[code] || code;
};

const LoadDetailsModal = ({
  selectedLoad,
  showSidebar,
  setShowSidebar,
  setShowGlobalOverlay,
  searchParams,
  theme,
  savingLoadId,
  deletingLoadId,
  isLoadSaved,
  handleSaveLoad,
  handleDeleteLoad,
  handleQuoteLoad,
  paradeAllowedActions,
}) => {
  // Hooks must be called before any early returns
  const paradeAllowedActionsLower = useMemo(() => {
    if (!Array.isArray(paradeAllowedActions)) return new Set();
    return new Set(paradeAllowedActions.map((a) => String(a).toLowerCase()));
  }, [paradeAllowedActions]);

  if (!showSidebar || !selectedLoad) {
    return null;
  }

  const isParade = selectedLoad?.data_source === "parade";
  const hasParadeAction = (action) => isParade && paradeAllowedActionsLower.has(action);

  return (
    <div
      className="fixed z-[1000] shadow-2xl overflow-y-auto
        md:top-0 md:right-0 md:w-[350px] md:h-full
        max-md:inset-x-0 max-md:bottom-0 max-md:rounded-t-2xl max-md:max-h-[75vh] max-md:animate-slide-up"
      style={{
        backgroundColor: "var(--surface)",
        color: "var(--text)",
        borderLeft: "1px solid #ccc",
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Load details"
    >
      {/* Mobile Drag Handle - Only on phones */}
      <div 
        className="md:hidden flex justify-center pt-2 pb-1 sticky top-0 z-10"
        style={{ backgroundColor: "var(--surface)" }}
      >
        <div className="w-12 h-1 rounded-full" style={{ backgroundColor: "rgba(255, 255, 255, 0.3)" }} />
      </div>

      {/* Close Button - Top Right */}
      <button
        onClick={() => {
          setShowSidebar(false);
          setShowGlobalOverlay(false);
        }}
        className="absolute top-4 right-4 bg-transparent border-none text-3xl cursor-pointer hover:opacity-80 transition-opacity z-20"
        style={{
          color: "var(--text)",
        }}
        aria-label="Close load details"
      >
        ×
      </button>

      <div className="p-5">
        <h3 style={{ fontSize: "1.25rem", fontWeight: 600, marginBottom: "1rem" }}>Load Details</h3>
      <p>
        <strong>Origin:</strong> {selectedLoad.origin_city},{" "}
        {selectedLoad.origin_state}
      </p>
      <p>
        <strong>Destination:</strong>{" "}
        {selectedLoad.destination_city},{" "}
        {selectedLoad.destination_state}
      </p>
      <p>
        <strong>Rate:</strong> $
        {selectedLoad.rate_per_mile_est?.toFixed(2)}
      </p>
      <p>
        <strong>Pay:</strong> ${selectedLoad.payment}
      </p>
      <p>
        <strong>Deadhead: </strong>
        {formatMiles(selectedLoad.origin_distance)}
      </p>
      <p>
        <strong>Total Trip Distance: </strong>
        {formatMiles(selectedLoad.distance)}
      </p>
      <p>
        <strong>Weight: </strong>
        {selectedLoad.weight} lbs
      </p>
      <p>
        <strong>Equipment: </strong>
        {getTruckTypeLabel(selectedLoad.equipment_type)}
      </p>
      <p>
        <strong>Length: </strong>
        {selectedLoad.length}
      </p>
      <p>
        <strong>Broker:</strong>{" "}
        {capitalizeCompanyName(selectedLoad.company_name)}
      </p>
      <p>
        <strong>Email:</strong>{" "}
        {isParade && selectedLoad.company_email ? (
          <a
            href={`mailto:${selectedLoad.company_email}`}
            style={{ color: "var(--info)" }}
          >
            {selectedLoad.company_email}
          </a>
        ) : (
          selectedLoad.company_email
        )}
      </p>
      <p>
        <strong>Phone:</strong> {selectedLoad.contact_phone}
      </p>
      <p>
        <strong>Source:</strong>{" "}
        {getDataSourceBadge(selectedLoad.data_source, theme)}
      </p>
      <p>
        <strong>{isParade ? "Reference ID" : "ID"}:</strong>{" "}
        {isParade ? selectedLoad.reference_id || selectedLoad.id : selectedLoad.id}
      </p>
      <p>
        <strong>Pickup Date:</strong> {selectedLoad.ship_date}
      </p>
      <p>
        <strong>Age:</strong> {selectedLoad.age && selectedLoad.age > 0 ? `${selectedLoad.age} hours` : "<1h"}
      </p>
      {selectedLoad.display_metadata && (
        <div
          style={{
            marginTop: "10px",
            padding: "10px",
            backgroundColor: "var(--background)",
            borderRadius: "6px",
            border: "1px solid var(--border)",
          }}
        >
          <h4 style={{ marginBottom: "8px" }}>Special Instructions</h4>
          <ul style={{ paddingLeft: "18px", margin: 0, listStyle: "disc" }}>
            {Object.entries(selectedLoad.display_metadata).map(
              ([key, value]) => (
                <li key={key} style={{ marginBottom: "4px" }}>
                  <strong>{key}:</strong> {String(value)}
                </li>
              ),
            )}
          </ul>
        </div>
      )}
      {/* Action Buttons */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "10px",
          marginTop: "15px",
          borderTop: "1px solid var(--border)",
          paddingTop: "15px",
        }}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            isLoadSaved(selectedLoad)
              ? handleDeleteLoad(selectedLoad)
              : handleSaveLoad(selectedLoad);
          }}
          style={{
            backgroundColor: isLoadSaved(selectedLoad)
              ? "var(--danger)"
              : "var(--primary)",
            color: "var(--button-text)",
            border: "none",
            borderRadius: "4px",
            padding: "8px 16px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flex: 1,
          }}
          disabled={
            savingLoadId === selectedLoad.entry_id ||
            deletingLoadId === selectedLoad.entry_id
          }
        >
          {savingLoadId === selectedLoad.entry_id ||
          deletingLoadId === selectedLoad.entry_id ? (
            <>
              <i className="fas fa-spinner fa-spin"></i>
              {isLoadSaved(selectedLoad)
                ? "Deleting..."
                : "Saving..."}
            </>
          ) : (
            <>
              <i
                className={
                  isLoadSaved(selectedLoad)
                    ? "fas fa-trash"
                    : "fas fa-save"
                }
              ></i>
              {isLoadSaved(selectedLoad)
                ? "Delete Load"
                : "Save Load"}
            </>
          )}
        </button>
        {console.log("selectedLoad", selectedLoad)}
        {isParade && hasParadeAction("quote") && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleQuoteLoad(selectedLoad);
            }}
            style={{
              backgroundColor: "#0069d9",
              color: "white",
              border: "none",
              borderRadius: "4px",
              padding: "8px 16px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flex: 1,
            }}
          >
            <i className="fas fa-hand-holding-usd"></i>
            Quote Load
          </button>
        )}
        <a
          href={buildSingleGoogleMapsURL(
            searchParams.origin_city,
            searchParams.origin_state,
            selectedLoad,
          )}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            backgroundColor: "#28a745",
            color: "white",
            border: "none",
            borderRadius: "4px",
            padding: "8px 16px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flex: 1,
            textDecoration: "none",
            justifyContent: "center",
          }}
        >
          <i className="fas fa-map-marked-alt"></i>
          View Route
        </a>
      </div>
      </div>
    </div>
  );
};

export default LoadDetailsModal;
